/*
 * TCE cart flow (1 Oct 2026). Loaded as a module by snippets/tce-cart-pickup.liquid (cart page),
 * sections/ready-today.liquid, sections/plan-ahead.liquid and blocks/pickup-picker.liquid.
 *
 * 1. Quick add: any <form data-tce-quick-add> is sent to /cart/add.js instead of posting to /cart.
 *    Horizon's cart UI is updated with the same standard event its own product form uses
 *    (CartLinesUpdateEvent, 'shopify:cart:lines-update'), then the cart drawer is opened. If the
 *    drawer isn't there, an inline "Added … · See bag" line is shown. Without JS (or if anything
 *    here fails to load) the form still posts natively.
 * 2. <tce-cart-pickup>: "Move everything to <day, window>" rewrites _pickup_date, _pickup_time and
 *    the translated visible pair on every line with /cart/change.js, one line at a time.
 * 3. Cart drawer: the drawer markup belongs to Horizon (snippets/cart-drawer.liquid) and has no
 *    pickup summary. sections/cart-drawer-section.liquid (the drawer's Section Rendering target)
 *    carries the summary in a <template data-tce-drawer-pickup>; this file copies it into the
 *    drawer after every cart change and on open. If a server-rendered summary is already in the
 *    drawer (see NOTES_flow.md, optional one-line patch), nothing is injected.
 * 4. Invalid-pickup guard (NEO-6): a line missing its pickup day or window (a cart permalink, a cleared
 *    property) makes the summary render with data-invalid. While one is on the page, the checkout
 *    button is paused, the express row is hidden (CSS, keyed on html[data-tce-pickup-invalid]) and a
 *    click or Enter is turned into "choose your pickup". A storefront guard only: a direct checkout
 *    URL still needs authoritative server enforcement. Guarded native controls fail closed without JS.
 */

let Events = null;
try {
  Events = await import('@shopify/events');
} catch (e) {
  Events = null;
}

const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const cartUrl = () => (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || `${root()}cart`;
const VISIBLE_NAMES = ['Pickup date', 'Pickup time', 'Fecha de recogida', 'Hora de recogida'];

function sectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

async function getCart() {
  const res = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!res.ok) throw new Error(`cart ${res.status}`);
  return res.json();
}

/* Tells Horizon (cart drawer, cart page, header count) that the cart changed. */
function announceCart(source, action, lines, cart, sections, didError = false) {
  if (!Events || !Events.CartLinesUpdateEvent) return false;
  const { CartLinesUpdateEvent } = Events;
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({ action, context: action === 'add' ? 'product' : 'cart', lines, promise: deferred.promise })
  );
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { items: cart.items, itemCount: cart.item_count, sections: sections || undefined, source: 'tce-cart', didError },
  });
  return true;
}

/* Store-time today (YYYY-MM-DD) and minutes after midnight. */
export function storeClock(tz) {
  const now = new Date();
  try {
    const parts = {};
    new Intl.DateTimeFormat('en-CA', {
      timeZone: tz || 'America/New_York',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(now).forEach((p) => (parts[p.type] = p.value));
    return { iso: `${parts.year}-${parts.month}-${parts.day}`, mins: (parseInt(parts.hour, 10) % 24) * 60 + parseInt(parts.minute, 10) };
  } catch (e) {
    return null;
  }
}

/* ---------- 1. Quick add ---------- */

function showAdded(form, ok, message) {
  const scope = form.closest('[data-tce-qa-scope]');
  const status = scope && scope.querySelector('[data-tce-added-status]');
  if (!status) return;
  status.textContent = '';
  const text = document.createElement('span');
  text.textContent = message;
  status.append(text);
  if (ok && status.dataset.bagLabel) {
    status.append(' · ');
    const a = document.createElement('a');
    a.href = cartUrl();
    a.textContent = status.dataset.bagLabel;
    status.append(a);
  }
  status.hidden = false;
}

async function quickAdd(form) {
  const button = form.querySelector('button[type="submit"], button:not([type])');
  if (button) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  }
  const fd = new FormData(form);
  const ids = sectionIds();
  if (ids.length) {
    fd.append('sections', ids.join(','));
    fd.append('sections_url', window.location.pathname);
  }
  const lines = [{ merchandiseId: String(fd.get('id')), quantity: Number(fd.get('quantity')) || 1 }];
  const meta = form.closest('[data-title]') || form;
  const title = meta.dataset.title || '';
  const status = form.closest('[data-tce-qa-scope]')?.querySelector('[data-tce-added-status]');
  let json = null;
  let res = null;
  try {
    res = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: fd,
      credentials: 'same-origin',
    });
    json = await res.json();
  } catch (e) {
    if (!res) {
      /* The request never reached the store: fall back to the form's own post (goes to /cart). */
      form.dataset.tceNative = 'true';
      form.submit();
    } else {
      /* The store answered but not with JSON: don't post again (it may have been added). */
      showAdded(form, false, (status && status.dataset.error) || '');
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
    return;
  }
  try {
    if (!res.ok || json.status) {
      showAdded(form, false, json.description || json.message || (status && status.dataset.error) || '');
      return;
    }
    const msg = (meta.dataset.tceAdded || '').replace('__TITLE__', title);
    showAdded(form, true, msg);
    const cart = await getCart();
    const told = announceCart(form, 'add', lines, cart, json.sections);
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (told && drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  } catch (e) {
    /* The dish is in the cart; only the cart UI refresh failed. The inline line already says so. */
  } finally {
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

if (!window.__tceQuickAdd) {
  window.__tceQuickAdd = true;
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[data-tce-quick-add]')) return;
    if (form.dataset.tceNative === 'true') return;
    event.preventDefault();
    quickAdd(form);
  });
}

/* ---------- 2. Move everything to one pickup ---------- */

let liveRegion = null;
function say(text) {
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', 'polite');
    document.body.append(liveRegion);
  }
  liveRegion.textContent = '';
  window.setTimeout(() => { liveRegion.textContent = text; }, 50);
}

class TceCartPickup extends HTMLElement {
  disconnectedCallback() {
    window.requestAnimationFrame(syncGuard);
  }

  connectedCallback() {
    window.requestAnimationFrame(syncGuard);
    if (this.wired) return;
    this.wired = true;
    this.addEventListener('click', (event) => {
      const btn = event.target instanceof Element ? event.target.closest('[data-move]') : null;
      if (btn && this.contains(btn)) this.move(btn);
    });
    this.hideStale();
  }

  /* A page left open: drop a target whose day has passed, or today's window that can no longer be made. */
  hideStale() {
    const c = storeClock(this.dataset.tz);
    if (!c) return;
    this.querySelectorAll('[data-move]').forEach((btn) => {
      const iso = btn.dataset.iso || '';
      const latest = parseInt(btn.dataset.latest || '', 10);
      btn.hidden = iso < c.iso || (iso === c.iso && Number.isFinite(latest) && c.mins > latest);
    });
  }

  async move(btn) {
    const target = {
      date: btn.dataset.date,
      iso: btn.dataset.iso,
      time: btn.dataset.time,
      dateProp: btn.dataset.dateProp || btn.dataset.date,
      timeProp: btn.dataset.timeProp || btn.dataset.time,
    };
    const strip = new Set(['_pickup_date_iso', ...VISIBLE_NAMES, this.dataset.propDate, this.dataset.propTime].filter(Boolean));
    const status = this.querySelector('[data-move-status]');
    const buttons = [...this.querySelectorAll('[data-move]')];
    buttons.forEach((b) => (b.disabled = true));
    if (status) status.textContent = this.dataset.moving || '';

    const dateOnly = item => (this.dataset.dateOnlyKeys || '').includes(`|${item.key}|`) || (this.hasAttribute('data-holiday-date-only') && item.properties?._tce_pickup_mode === 'date-only' && item.properties?._tce_schedule === 'thanksgiving');
    const needs = (item) => {
      const p = item.properties || {};
      return p._pickup_date !== target.date || (!dateOnly(item) && p._pickup_time !== target.time) || (target.iso && p._pickup_date_iso !== target.iso);
    };
    try {
      let cart = await getCart();
      let last = null;
      const tried = new Set();
      const ids = sectionIds();
      for (let guard = 0; guard < 60; guard += 1) {
        const pending = cart.items.filter(needs);
        if (!pending.length) break;
        const item = pending[0];
        if (tried.has(item.key)) throw new Error('line did not change');
        tried.add(item.key);
        const props = {};
        Object.entries(item.properties || {}).forEach(([k, v]) => {
          const preserveTime = dateOnly(item) && ['_pickup_time', 'Pickup time', 'Hora de recogida', this.dataset.propTime].includes(k);
          if ((!strip.has(k) || preserveTime) && v !== null && v !== '') props[k] = v;
        });
        if (dateOnly(item)) {
          props._tce_pickup_mode = 'date-only';
          if(btn.dataset.holidayStart&&btn.dataset.holidayEnd&&btn.dataset.holidayHours){
            delete props['Pickup hours']; delete props['Horario de recogida'];
            props._tce_pickup_window_start=btn.dataset.holidayStart;props._tce_pickup_window_end=btn.dataset.holidayEnd;props._tce_pickup_hours=btn.dataset.holidayHours;
            props[document.documentElement.lang.startsWith('es')?'Horario de recogida':'Pickup hours']=btn.dataset.holidayHours;
          }
        }
        props._pickup_date = target.date;
        if (!dateOnly(item)) props._pickup_time = target.time;
        if (/^\d{4}-\d{2}-\d{2}$/.test(target.iso || '')) props._pickup_date_iso = target.iso;
        if (this.dataset.propDate) props[this.dataset.propDate] = target.dateProp;
        if (this.dataset.propTime && !dateOnly(item)) props[this.dataset.propTime] = target.timeProp;
        const body = { id: item.key, quantity: item.quantity, properties: props };
        if (pending.length === 1 && ids.length) {
          body.sections = ids.join(',');
          body.sections_url = window.location.pathname;
        }
        const res = await fetch(`${root()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
          credentials: 'same-origin',
        });
        const json = await res.json();
        if (!res.ok || json.status) throw new Error(json.description || json.message || 'change failed');
        cart = json;
        last = json;
      }
      if (cart.items.some(needs)) throw new Error('lines left');
      const moved = (this.dataset.moved || '').replace('__PICKUP__', btn.dataset.pickup || '');
      say(moved);
      const told = announceCart(this, 'update', cart.items.map((i) => ({ id: i.key, quantity: i.quantity })), cart, last && last.sections);
      if (!told) window.location.reload();
    } catch (e) {
      buttons.forEach((b) => (b.disabled = false));
      if (status) status.textContent = this.dataset.error || '';
      /* Something may have moved before the error: let Horizon re-read the cart. */
      try {
        const cart = await getCart();
        announceCart(this, 'update', [], cart, null);
      } catch (err) { /* keep the error message */ }
    }
  }
}
if (!customElements.get('tce-cart-pickup')) customElements.define('tce-cart-pickup', TceCartPickup);

/* ---------- 4. Invalid-pickup guard ---------- */

const CHECKOUT_SELECTOR = 'button[name="checkout"], .cart__checkout-button';

/* A line is valid with BOTH a day and a window, from the canonical pair or the visible pair. */
export function linePickupValid(properties, { dateOnly = false } = {}) {
  const p = properties || {};
  const pick = (names) => {
    for (const n of names) {
      const v = p[n];
      if (v !== undefined && v !== null && String(v).trim() !== '') return true;
    }
    return false;
  };
  return (
    pick(['_pickup_date', 'Pickup date', 'Fecha de recogida']) &&
    (dateOnly || pick(['_pickup_time', 'Pickup time', 'Hora de recogida']))
  );
}

export function cartPickupInvalid(cart) {
  const keys = document.querySelector('tce-cart-pickup[data-holiday-date-only]')?.dataset.dateOnlyKeys || '';
  return !!(cart && Array.isArray(cart.items) && cart.items.some((i) => !linePickupValid(i.properties, { dateOnly: keys.includes(`|${i.key}|`) })));
}

function invalidSummary() {
  return document.querySelector('tce-cart-pickup[data-invalid]') || document.querySelector('tce-cart-pickup[data-schedule-invalid]') || [...document.querySelectorAll('tce-cart-pickup')].find(summary => summary.querySelector('tce-availability-calendar') && !summary.hasAttribute('data-schedule-checked'));
}

document.addEventListener('tce:schedule-change', () => syncGuard());

/* Mirrors the server-rendered summary onto <html> and the checkout buttons. */
export function syncGuard() {
  const summaries = [...document.querySelectorAll('tce-cart-pickup')];
  const pending = document.querySelector('[data-tce-checkout-disabled]') && summaries.length === 0;
  const invalid = !!invalidSummary() || !!pending;
  const html = document.documentElement;
  if (invalid) html.setAttribute('data-tce-pickup-invalid', '');
  else html.removeAttribute('data-tce-pickup-invalid');
  document.querySelectorAll(CHECKOUT_SELECTOR).forEach((b) => {
    if (invalid) {
      b.setAttribute('aria-disabled', 'true');
      if (!b.disabled) { b.disabled = true; b.dataset.tceCheckoutDisabled = 'true'; }
    } else {
      if (b.getAttribute('aria-disabled') === 'true') b.removeAttribute('aria-disabled');
      if (b.dataset.tceCheckoutDisabled === 'true') { b.disabled = false; delete b.dataset.tceCheckoutDisabled; }
    }
  });
  document.querySelectorAll('[data-tce-checkout-wallets]').forEach(row => { row.hidden = invalid; });
}

function explainInvalid() {
  const summary = invalidSummary();
  if (!summary) return;
  const guard = summary.querySelector('[data-invalid-guard]') || summary.querySelector('tce-availability-calendar');
  say(summary.dataset.invalidMsg || summary.dataset.scheduleInvalidMsg || '');
  if (guard) {
    guard.scrollIntoView({ block: 'center' });
    guard.focus({ preventScroll: true });
  }
}

if (!window.__tcePickupGuard) {
  window.__tcePickupGuard = true;
  document.addEventListener(
    'click',
    (event) => {
      const btn = event.target instanceof Element ? event.target.closest(CHECKOUT_SELECTOR) : null;
      if (!btn || !invalidSummary()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      explainInvalid();
    },
    true
  );
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const sub = event.submitter;
      if (!(form instanceof HTMLFormElement) || !sub || !sub.matches || !sub.matches(CHECKOUT_SELECTOR)) return;
      if (!invalidSummary()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      explainInvalid();
    },
    true
  );
}

/* ---------- 3. Pickup summary in the cart drawer ---------- */

const drawerPickup = {
  html: null,
  version: 0,
  mounted: -1,
  observer: null,

  drawer() {
    return document.querySelector('theme-drawer#cart-drawer');
  },

  async refresh(sections) {
    const drawer = this.drawer();
    if (!drawer) return;
    let html = sections && sections['cart-drawer-section'];
    try {
      if (!html) {
        const res = await fetch(`${root()}?section_id=cart-drawer-section`, { credentials: 'same-origin' });
        html = await res.text();
      }
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const tpl = doc.querySelector('template[data-tce-drawer-pickup]');
      this.html = tpl ? tpl.innerHTML.trim() : '';
      this.version += 1;
      this.mount();
    } catch (e) { /* leave the drawer as Horizon rendered it */ }
  },

  mount() {
    const drawer = this.drawer();
    if (!drawer || this.html === null) return;
    /* A summary rendered by the server inside the drawer wins. */
    if (drawer.querySelector('tce-cart-pickup:not([data-injected])')) return;
    const host = drawer.querySelector('.cart-drawer__items');
    const current = drawer.querySelector('tce-cart-pickup[data-injected]');
    if (!host || !this.html) {
      if (current) current.remove();
      this.mounted = this.version;
      return;
    }
    if (current && this.mounted === this.version && host.contains(current)) return;
    const box = document.createElement('div');
    box.innerHTML = this.html;
    const el = box.querySelector('tce-cart-pickup');
    if (!el) return;
    el.setAttribute('data-injected', '');
    if (current) current.remove();
    host.prepend(el);
    this.mounted = this.version;
  },

  init() {
    const drawer = this.drawer();
    if (!drawer || this.observer) return;
    /* Horizon re-renders the drawer contents after every cart change, which drops our node. */
    this.observer = new MutationObserver(() => {
      if (this.html && !drawer.querySelector('tce-cart-pickup')) {
        this.mounted = -1;
        this.mount();
      }
    });
    this.observer.observe(drawer, { childList: true, subtree: true });
    drawer.addEventListener('theme-drawer:open', () => {
      if (this.html === null) this.refresh(null);
      else this.mount();
    });
    if (drawer.hasAttribute('open')) this.refresh(null);
  },
};

document.addEventListener('shopify:cart:lines-update', (event) => {
  const promise = event.promise;
  if (!promise || typeof promise.then !== 'function') return;
  promise
    .then((result) => {
      const sections = result && result.detail && result.detail.sections;
      /* After Horizon's own morph of the drawer. */
      window.requestAnimationFrame(() => {
        drawerPickup.refresh(sections);
        syncGuard();
      });
    })
    .catch(() => {});
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    drawerPickup.init();
    syncGuard();
  });
} else {
  drawerPickup.init();
  syncGuard();
}
